import React, { useEffect, useState } from 'react'
import { Badge, Button, Card, CardBody, CardHeader, Col, Placeholder, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Label } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import './index.css';
import BreadCrumbs from '../../../../components/BreadCrumbs';
import StatusBadge from '../../../../components/SideNavBar/StatusBatch';
import TableLoadingComponent from '../../../../components/TableLoadingComponent';
import { ReturnRequestsDetails, useLazyGetReturnRequestByIdQuery, useReturnRequestStatusUpdateMutation } from '../../../../redux/services/returnRequestService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

const ViewReturnRequest = () => {
    const location = useLocation();
    const request = location.state?.request as ReturnRequestsDetails;

    const refundRequestId = request.id;
    const requestId = refundRequestId;

    const [requestStatus, setRequestStatus] = useState<string>(request?.status);
    const [isRequestApproving, setIsRequestApproving] = useState<boolean>(false);
    const [isRequestRejecting, setIsRequestRejecting] = useState<boolean>(false);

    const [
        triggerGetReturnRequestById,
        { isLoading: isLoadingReturnRequestData, data: returnRequestData },
    ] = useLazyGetReturnRequestByIdQuery();

    useEffect(() => {
        if (refundRequestId) {
            triggerGetReturnRequestById(refundRequestId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refundRequestId, triggerGetReturnRequestById]);

    const [returnRequestStatusUpdate] = useReturnRequestStatusUpdateMutation();

    const handleRequest = async (status: "Approved" | "Rejected") => {
        if (isRequestApproving || isRequestRejecting) return;
        if (status === "Approved") {
            setIsRequestApproving(true);
        } else {
            setIsRequestRejecting(true);
        }
        try {
            const response = await returnRequestStatusUpdate({ requestId, status }).unwrap();
            if (response?.updatedStatus) {
                setRequestStatus(response.updatedStatus);
                toast.success(
                    `Request ${status === "Approved" ? "refunded" : "rejected"} successfully`
                );
                setIsRequestApproving(false);
                setIsRequestRejecting(false);
            } else {
                setIsRequestApproving(false);
                setIsRequestRejecting(false);
                throw new Error("Invalid response format");
            }
        } catch (error: any) {
            setIsRequestApproving(false);
            setIsRequestRejecting(false);
            console.error("Error updating request status:", error);
            const errorMessage = error?.data;
            Swal.fire("Error", errorMessage, "error");
        }
    };

    const showReturnRequestModal = (type: "Approve" | "Reject") => {
        const action = type === "Approve" ? "Accept" : "Reject";
        const status = type === "Approve" ? "Approved" : "Rejected";

        Swal.fire({
            title: `Confirm ${action}`,
            text: `Are you sure you want to ${action.toLowerCase()} this request?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: `Yes, ${action}!`,
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc3545",
        }).then((result) => {
            if (result.isConfirmed) {
                handleRequest(status);
            }
        });
    };

    return (
        <>
            <BreadCrumbs hasSubMenus={true} />
            {isLoadingReturnRequestData ? (
                <>
                    <h5 className='mt-5'>Request Details</h5>
                    <Row>
                        <Col lg="12">
                            <Card className="w-100" style={{ height: 150 }}>
                                <CardBody>
                                    <table className="info-table mt-4">
                                        <tbody>
                                            <tr>
                                                <td className='value'>
                                                    <Placeholder animation='wave'>
                                                        <Placeholder xs={12} />
                                                    </Placeholder>
                                                </td>
                                                <td className='label'>
                                                    <Placeholder animation='wave'>
                                                        <Placeholder xs={12} />
                                                    </Placeholder>
                                                </td>
                                            </tr>
                                            <tr className='mt-5 mb-5'>
                                                <td className='value'>
                                                    <Placeholder animation='wave'>
                                                        <Placeholder xs={12} />
                                                    </Placeholder>
                                                </td>
                                                <td className='label'>
                                                    <Placeholder animation='wave'>
                                                        <Placeholder xs={12} />
                                                    </Placeholder>
                                                </td>
                                            </tr>
                                            <tr className='mt-5 mb-5'>
                                                <td className='value'>
                                                    <Placeholder animation='wave'>
                                                        <Placeholder xs={12} />
                                                    </Placeholder>
                                                </td>
                                                <td className='label'>
                                                    <Placeholder animation='wave'>
                                                        <Placeholder xs={12} />
                                                    </Placeholder>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <Card className="order-card">
                        <TableLoadingComponent numColumns={5} />
                    </Card>
                </>
            ) : (
                <div className="order-view">
                    <div className='d-flex justify-content-left align-items-baseline gap-3'>
                        <h4 className="mt-3">Return Request :</h4>
                        <StatusBadge status={requestStatus} />
                    </div>

                    {requestStatus !== "Approved" && requestStatus !== "Rejected" && (
                        <Card style={{ minHeight: "10px" }} className='py-2'>
                            <CardBody>
                                <Label className="mt-1">Remark</Label>
                                <textarea
                                    placeholder="Add Comment"
                                    className="w-100 order-refund-text-area p-4"
                                />
                                <div className="d-flex justify-content-end gap-3 mt-3">
                                    <Button variant="warning" className="mr-2" onClick={() => showReturnRequestModal("Approve")} disabled={isRequestApproving || isRequestRejecting}>
                                        {isRequestApproving ? (<><FontAwesomeIcon icon={faSpinner} spin className='me-2'/> Processing...</>) : 'Approve'}
                                    </Button>
                                    <Button variant="danger" onClick={() => showReturnRequestModal("Reject")} disabled={isRequestApproving || isRequestRejecting}>
                                        {isRequestRejecting ? (<><FontAwesomeIcon icon={faSpinner} spin className='me-2'/> Processing...</>) : 'Reject'}
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    )}

                    <h5 className='mt-5'>Request Details</h5>
                    <Row>
                        <Col lg="12">
                            <Card className="w-100" style={{ height: 150 }}>
                                <CardBody>
                                    <table className="info-table mt-4">
                                        <tbody>
                                            <tr>
                                                <td className="label">Cart Name</td>
                                                <td className="value">: {returnRequestData?.cart.displayName ?? "Loading..."}</td>
                                            </tr>
                                            <tr>
                                                <td className="label">Restaurant Name</td>
                                                <td className="value">: {returnRequestData?.restaurantName}</td>
                                            </tr>
                                            <tr>
                                                <td className="label">Status</td>
                                                <td className="order-status-style value">
                                                    :{" "}
                                                    <Badge
                                                        bg={
                                                            requestStatus === "Pending"
                                                                ? "warning"
                                                                : requestStatus === "Approved"
                                                                    ? "success"
                                                                    : requestStatus ===
                                                                        "Rejected"
                                                                        ? "danger" : "secondary"
                                                        }
                                                        className="px-3 py-2"
                                                    >
                                                        <div style={{ fontSize: 12 }}>
                                                            {requestStatus === "Pending"
                                                                ? "Pending"
                                                                : requestStatus === "Approved"
                                                                    ? "Approved"
                                                                    : requestStatus ===
                                                                        "Rejected"
                                                                        ? "Rejected" : ""
                                                            }
                                                        </div>
                                                    </Badge>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    <Card className="order-card">
                        <CardHeader className="order-card-header">
                            <h6>Return Items</h6>
                        </CardHeader>
                        <CardBody>
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Item Name</th>
                                        <th>Per Item Price (LKR)</th>
                                        <th>Quantity</th>
                                        <th>Total (LKR)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {returnRequestData?.items?.map((item, index) => (
                                        <tr key={item.menuItemId} className="custom-item-row border-none">
                                            <td>{index + 1} .</td>
                                            <td>{item.displayName}</td>
                                            <td>{item.unitPrice?.toFixed(2)}</td>
                                            <td>{item.quantity}</td>
                                            <td>{(item.unitPrice * item.quantity).toFixed(2)}</td>
                                        </tr>
                                    ))}

                                    {/* Footer row with total sum */}
                                    {(returnRequestData?.items?.length ?? 0) > 0 && (
                                        <tr className="total-row">
                                            <td colSpan={4} className="text-end fw-bold">Grand Total:</td>
                                            <td className="fw-bold">
                                                {(returnRequestData?.items ?? [])
                                                    .reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0)
                                                    .toFixed(2)}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </CardBody>
                    </Card>
                </div>
            )}
        </>
    )
}

export default ViewReturnRequest;
